import React from 'react';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useRegion } from '../../context/RegionContext';

const CartItem = ({ item }) => {
  const { removeFromCart, updateQuantity } = useCart();
  const { region, getCurrencySymbol } = useRegion();

  const price = region === 'US' ? item.priceUSD : item.priceCAD;
  const itemTotal = price * item.quantity;
  
  const itemId = item.cartItemId || item.id;

  return (
    <div className="flex gap-4 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-300">
      {/* Product Image */}
      <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-contain bg-gray-50"
        />
      </div>

      {/* Product Details */}
      <div className="flex-1 flex flex-col justify-between min-w-0">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg mb-1 truncate">
            {item.name}
          </h3>
          
          <p className="text-sm text-gray-600 mb-2">
            {item.style}
          </p>

          {/* Color Badge - Text Only */}
          {item.options?.color && (
            <div className="inline-flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full font-medium">
                {item.options.color}
              </span>
            </div>
          )}

          {/* Handle Badge */}
          {item.options?.handle && (
            <div className="inline-flex items-center gap-2 mb-2 ml-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
                Handle {item.options.handle}
              </span>
            </div>
          )}

          {/* Size Badge */}
          {item.options?.size && (
            <div className="inline-flex items-center gap-2 mb-2 ml-2">
              <span className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full font-medium">
                Size {item.options.size}
              </span>
            </div>
          )}
        </div>

        {/* Quantity Controls */}
        <div className="flex items-center gap-3">
          <div className="flex items-center border border-gray-300 rounded-lg">
            <button
              onClick={() => updateQuantity(itemId, item.quantity - 1)}
              className="p-2 hover:bg-gray-100 transition-colors duration-300"
              disabled={item.quantity <= 1}
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="px-4 font-medium">{item.quantity}</span>
            <button
              onClick={() => updateQuantity(itemId, item.quantity + 1)}
              className="p-2 hover:bg-gray-100 transition-colors duration-300"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Price */}
          <div className="ml-auto text-right">
            <p className="font-bold text-gray-900 text-lg">
              {getCurrencySymbol()}{itemTotal.toLocaleString()}
            </p>
            {item.quantity > 1 && (
              <p className="text-sm text-gray-500">
                {getCurrencySymbol()}{price} each
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Remove Button */}
      <button
        onClick={() => removeFromCart(itemId)}
        className="p-2 h-fit hover:bg-red-50 rounded-lg transition-colors duration-300 group"
        aria-label="Remove item"
      >
        <Trash2 className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors duration-300" />
      </button>
    </div>
  );
};

export default CartItem;